import React, { createContext, useContext, useState } from 'react';
import { sendRequest } from '../api';
import { useUser } from './UserProvider';
import Loader from '../components/Loader';

const VideosContext = createContext();

export function useVideos() {
  const context = useContext(VideosContext);
  if (!context) {
    throw new Error('useVideos must be used within a VideosProvider');
  }
  return context;
}

export function VideosProvider({ children }) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [videos, setVideos] = useState({});
  const [pageToken, setPageToken] = useState({});
  const [erroredPlatforms, setErroredPlatforms] = useState(new Map());
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadId, setDownloadId] = useState(null);

  // Fetch videos for a specific platform
  const fetchVideosForPlatform = async (platformData) => {
    try {
      const params = {
        uid: platformData.uid,
      }
      if (pageToken[platformData.uid]) {
        params.page_token = pageToken[platformData.uid]
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const endpoint = `/videos/${platformData.name}`;
      const response = await sendRequest(endpoint, "GET", params);

      if (response.error) {
        // Store the error message for this platform
        setErroredPlatforms(prev => new Map(prev).set(platformData.uid, response.error));
        
        // Clear videos and tokens for this platform
        setVideos(prev => ({
          ...prev,
          [platformData.uid]: []
        }));
        setPageToken(prev => ({
          ...prev,
          [platformData.uid]: null
        }));
        return;
      }

      setVideos(prev => ({
        ...prev,
        [platformData.uid]: pageToken[platformData.uid] 
          ? [...(prev[platformData.uid] || []), ...response.videos.map(video => ({ 
              ...video, 
              platform: platformData.name,
              username: platformData.username,
              uid: platformData.uid
            }))]
          : response.videos.map(video => ({ 
              ...video, 
              platform: platformData.name,
              username: platformData.username,
              uid: platformData.uid
            }))
      }));

      setPageToken(prev => ({
        ...prev,
        [platformData.uid]: response.page_token
      }));

      // Remove from errored platforms if successful
      setErroredPlatforms(prev => {
        const newMap = new Map(prev);
        newMap.delete(platformData.uid);
        return newMap;
      });
    } catch (error) {
      // Store the error message for this platform
      setErroredPlatforms(prev => new Map(prev).set(platformData.uid, error.message));
      
      // Clear videos and tokens for this platform
      setVideos(prev => ({
        ...prev,
        [platformData.uid]: []
      }));
      setPageToken(prev => ({
        ...prev,
        [platformData.uid]: null
      }));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more videos for current platform
  const loadMore = async (platformData) => {
    if (pageToken[platformData.uid]) {
      try {
        await fetchVideosForPlatform(platformData);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  // Get error message for a specific platform
  const getPlatformError = (platformUid) => {
    return erroredPlatforms.get(platformUid);
  };

  const value = {
    videos,
    loading,
    loadingMore,
    isDownloading,
    downloadId,
    pageToken,
    erroredPlatforms,
    loadMore,
    fetchVideosForPlatform,
    getPlatformError,
    setDownloadId,
    setIsDownloading
  };

  return (
    <VideosContext.Provider value={value}>
      {children}
    </VideosContext.Provider>
  );
}
